/**
 * uiCopy — 워크스페이스 화면 상시 설명 문구 SSoT.
 *
 * ## 이 파일이 소유하는 것
 *
 * 화면에 **항상 붙어 있는** "누르면 무엇이 남는가 · 어디로 가는가 · 이 숫자는 무슨 기준인가".
 * 정시·수시가 같은 액션(지원 후보 담기·분석 결과 저장)을 갖고 있어 문구를 컴포넌트마다
 * 적으면 곧 어긋난다 — 여기 한 벌만 두고 화면은 수치만 채운다.
 *
 * 단계별 화면 안내(오버레이 투어)를 대체한다(064). 오버레이는 읽기로 결심해야 작동하고
 * 모바일 도달률이 낮다 — 설명은 그 자리에 상시로 둔다.
 *
 * ## 소유 경계
 *
 * - `disclaimer.ts` — 예측 한계·참고 활용 **고지**(면책). 여기가 아니다.
 * - `workspaceNav.ts` — GNB 라벨. 목적지 이름은 그 라벨을 그대로 쓴다.
 * - `docs/fo-service-glossary.md` — 대표 용어. 여기 문구는 그 표를 따르고 재정의하지 않는다.
 * - 서버 소유 산문(밴드 라벨·heldReason·cutNature)은 대상 밖.
 *
 * ## 카피 규칙
 *
 * - 결과 지향 — "무슨 버튼인지"가 아니라 "누르면 뭐가 남는지". 목적지는 GNB 라벨로 지목한다.
 * - 이동·불가역은 반드시 말한다(저장이 화면을 떠나는데 예고가 없으면 사용자가 길을 잃는다).
 * - 위치("오른쪽에")로 설명하지 않는다 — 폭에 따라 배치가 바뀐다.
 * - 사실만 적는다: 담기 → AI 종합분석 지원 후보(정시·수시 합산 한도) / 저장 → 불변 리포트·즉시 이동·
 *   리포트 보관함 › 성적 분석 결과 / 수시 6장 → 담아 둔 후보와 무관 / 이용권 → 보유 대학 1곳당 M회(서버 정책값)·공용.
 */

import type { CardOutlook } from '#/api/susiCards';
import type { CoverageWarning } from '#/api/analysisCard';
import { GRADE_OPTIONS, labelOf } from '#/data/analysis/ipsiCardOptions';

/** 지원 후보 담기 — 정시·수시 공용. */
export const APPLY_CARD_COPY = {
  button: '지원 후보로 담기',
  buttonSaved: '지원 후보에 담김',
  /** 버튼 아래 보조문. count 는 로딩 전 null 이면 괄호를 생략한다. */
  note(count: number | null, limit: number | null): string {
    const tally = count != null ? ` (${count}${limit != null ? `/${limit}` : ''}개)` : '';
    return `담은 대학·학과는 AI 종합분석의 지원 후보에 모입니다${tally}. 언제든 뺄 수 있습니다.`;
  },
  toast: '지원 후보에 담았습니다. AI 종합분석에서 함께 볼 수 있습니다.',
  /** 409 중복 담기 안내. */
  duplicateTitle: '이미 담긴 후보입니다',
  duplicateTail: '은(는) 이미 지원 후보에 있습니다. AI 종합분석에서 함께 볼 수 있습니다.',
  /** 좁은 폭 하단 바의 한 줄 요약(버튼 두 개의 목적지 대비). */
  compactHint: (count: number | null, limit: number | null): string =>
    `담기 → AI 종합분석 후보${count != null ? ` (${count}${limit != null ? `/${limit}` : ''})` : ''} · 저장 → 리포트 보관함`,
} as const;

/**
 * 분석 결과 저장 — 정시·수시 공용 버튼. 저장은 화면을 떠나고 되돌릴 수 없다.
 *
 * 보조문은 대상 알갱이가 달라 둘로 나뉜다(070): 수시는 전형 전체/학과 두 알갱이 공용(`note`), 정시는 학과 1건 고정(`noteJeongsi`).
 */
export const SAVE_DIAGNOSIS_COPY = {
  button: '분석 결과 저장',
  /** 수시(`SusiActionRail`) — 전형 전체 또는 고른 학과. */
  note: '지금 보고 있는 전형(학과를 골랐다면 그 학과)의 분석 결과가 리포트로 저장되고 그 화면으로 이동합니다. 저장한 리포트는 수정할 수 없으며, 리포트 보관함 › 성적 분석 결과에서 다시 볼 수 있습니다.',
  /** 정시(`JeongsiDetailPanel`) — 지금 보고 있는 학과 1건(070). */
  noteJeongsi:
    '이 학과의 분석 결과(내 위치·연도별 입시결과·해석)가 리포트로 저장되고 그 화면으로 이동합니다. 저장한 리포트는 수정할 수 없으며, 리포트 보관함 › 성적 분석 결과에서 다시 볼 수 있습니다.',
  /** 수시 — 내신 미저장 시 비활성 사유. */
  needsNaesin: '분석 결과 저장은 내신을 저장한 뒤 할 수 있습니다.',
} as const;

/**
 * 학과형 정시 진단 리포트(070) — 저장 당시 해석이 없었던 이유. 이후 자료를 보유해도 이 리포트에는 해석이 생기지 않는다(불변).
 * "지금 기준 해석"은 하단 [정시 분석에서 지금 기준으로 보기]가 안내한다 — 구매 CTA 를 리포트에 두지 않는다(읽기 전용).
 */
export const DIAGNOSIS_INTERPRETATION_NOTE = {
  locked:
    '저장 당시 이 대학 자료를 보유하지 않아 해석이 포함되지 않았습니다. 지금 기준 해석은 아래 [정시 분석에서 지금 기준으로 보기]에서 확인할 수 있습니다.',
  unavailable:
    '저장 당시 해석을 만들지 못해 포함되지 않았습니다. 지금 기준 해석은 아래 [정시 분석에서 지금 기준으로 보기]에서 확인할 수 있습니다.',
} as const;

/**
 * 보관함 카드 표식(072) — 학과형 진단 카드에 붙는 "이 학과에 AI 해석이 있다" 배지 라벨. 시각은 카드가 `formatKstDate` 로 붙인다.
 * 단독 AI 카드의 부제("{전형} · AI 해석")는 서버 완성형이라 여기서 재조립하지 않는다.
 */
export const ARCHIVE_OPINION_MARK = 'AI 해석';

/**
 * 학과형 진단 상세의 AI 층(072) — `JeongsiReadingSection readonly` 의 ai-header/ai-footer 슬롯 문구.
 * "지문"·"fingerprint"·"크레딧" 은 회피어(용어집 4.1). 배지는 "이 진단"(저장 성적)과 "이 해석"(생성 성적)이 다름을 말한다.
 */
export const DIAGNOSIS_OPINION_COPY = {
  /** AI 층 캡션 — kst 는 `formatKst` 결과(YYYY.MM.DD HH:mm). */
  generatedAt: (kst: string): string => `${kst} 생성`,
  basisDiffers: '이 진단과 다른 성적 기준',
  history: (n: number): string => `이전 해석 ${n}건`,
  historyItem: (kst: string): string => `${kst} 해석 보기`,
} as const;

/** 학과형 리포트의 [지금 기준으로 보기]로 정시 화면에 들어왔는데 그 학과가 현재 목록에 없을 때(070 FR-014) — 1회성 인라인 안내. */
export const DIAGNOSIS_DEEPLINK_MISSING =
  '저장 당시 학과를 지금 목록에서 찾을 수 없어 조건만 적용했습니다.';

/** 수시 분석 화면의 "수시 6장 전략" 진입 — 담아 둔 후보와 별개 경로임을 말한다. */
export const SUSI_CARDS_ENTRY_COPY = {
  title: '수시 6장 전략',
  body: '여기서 고른 대학이나 담아 둔 후보와 별개로, 저장한 내신과 계열·지역 조건으로 AI가 6장을 새로 구성합니다.',
  cta: '수시 6장 전략 만들기',
} as const;

/**
 * AI 이용권 적립·공용 규칙 — 툴팁(호버)에만 두면 모바일에서 영영 못 본다. 본문에 쓴다.
 * M 은 `availability.grantPerUniversity`(서버 정책값) — 값이 오기 전에는 이 문장을 그리지 않는다(폴백 재기입 금지, 073).
 */
export const creditRule = (m: number): string =>
  `이용권은 자료를 보유한 대학 1곳당 ${m}회 적립되며, 정시 AI 상세 해석과 수시 6장 전략이 함께 씁니다.`;

/**
 * 랜딩(WS-00) 이용권 스트립(075) — 로그인은 `creditRule(m)`, 비로그인은 정책값 없이 성격만 말한다
 * (M 은 로그인 전용 가용성 API 에서만 오고, FE 가 폴백 숫자를 재기입하지 않는다 — 073).
 */
export const LANDING_CREDIT_COPY = {
  label: 'AI 이용권',
  anon: 'AI 해석과 수시 6장 전략은 AI 이용권으로 이용합니다. 이용권은 자료를 보유한 대학마다 적립되며, 가입과 첫 분석은 무료입니다.',
} as const;

/**
 * 후기 조건 안내(073 FR-007) — 무료로 받은 대학의 적립 조건. 세 진입점(정시 해석·수시 6장·재검토 바)과 헤더 칩이 같은 문장을 쓴다.
 * 화면 노출은 "AI 이용권"·"후기" 만 — "크레딧"·"원장"·"게이트" 금지(용어집).
 */
export const REVIEW_GATE_COPY = {
  title: '후기를 작성하면 AI 이용권이 생깁니다',
  body: (m: number): string =>
    `무료로 받은 자료의 AI 분석은 후기 작성 후 이용할 수 있습니다. 후기 1건을 쓰면 무료로 받은 대학 1곳당 ${m}회가 바로 적립됩니다.`,
  cta: '후기 작성하기',
  barHint: '무료로 받은 자료는 후기 작성 후 AI 재검토를 받을 수 있어요.',
  badgeTitle: (m: number): string => `후기를 작성하면 무료로 받은 대학 1곳당 ${m}회가 적립됩니다.`,
} as const;

/** 판정 보류("자료 부족")의 뜻 — "지원 불가"로 오독되는 것을 막는다. */
export const HELD_NOTE =
  '합격선 자료가 없거나 기준을 확인 중이라 분석하지 않았습니다. 지원할 수 없다는 뜻이 아니라 비교할 자료가 부족하다는 뜻입니다.';
/** 요약 카드 안에 들어가는 축약형. */
export const HELD_NOTE_SHORT = '비교할 자료가 부족해 분석하지 않음';

/** 내신 평균의 과목 범위(기획 FSD 확정: 국·영·수·사·과). */
export const MAIN_SUBJECTS_LABEL = '주요 교과(국·영·수·사·과)';

/** 수시 90% 지표 대표어(사전 §4.1) — 서버 정의 "최근 입시결과를 통계적으로 추정한 합격선". */
export const SUSI_CUT_LABEL = '추정 합격선';
export function susiCutDefinition(year: number): string {
  return `${year}학년도 합격자 90%가 이 등급 안에 들었습니다. 최근 입시결과를 통계적으로 추정한 값이라 대학 공식 발표와 다를 수 있습니다.`;
}

/** 정시 해석 깊이 BASIC 의 뜻 — 배지 title 에만 있던 설명을 본문으로 올린다. */
export const BASIC_INTERPRETATION_NOTE =
  '이 대학은 모집요강 서술 원문이 없어 반영 기준 범위에서만 해석했습니다.';

/**
 * 정시 학과 상세 해석 섹션(071) — 제목은 하나("해석"), 심화 진입은 하나("더 깊게 보기").
 * 섹션(`JeongsiReadingSection`)과 심화층 카드(`JeongsiDeptOpinion`)가 함께 쓴다.
 * "잔여 N회"·"잔여 횟수"는 회피어 — 대표어는 "AI 이용권"(용어집 3.9).
 */
export const READING_SECTION_COPY = {
  title: '해석',
  /** 심화층 보조 라벨 — h3 가 아닌 캡션(FR-004). AI 가 만든 것임은 이 라벨과 컨펌·설명 카드가 말한다. */
  aiLabel: 'AI 상세 해석',
  deeper: '더 깊게 보기',
  /** 이용권 제한 활성 시에만 CTA 옆에 붙는 보조 표기(FR-006). */
  deeperCost: '이용권 1회',
  baseLabel: '기본 해석',
  expand: '펼치기',
  collapse: '접기',
  balance: (n: number): string => `이용권 ${n}회`,
} as const;

/**
 * AI 재검토 확인 모달 본문(063) — 쿼터 활성이면 차감·잔여 안내, 아니면 보관 안내만.
 * 074 는 이 문장 앞에 커버리지 경고(`NAESIN_COVERAGE_COPY.confirmBody`)를 이어 붙인다(모달 1개).
 */
export const reviewConfirmBody = (quotaEnabled: boolean, balance: number | null): string =>
  quotaEnabled
    ? `AI 재검토 1회에 이용권 1회가 차감됩니다(잔여 ${balance ?? 0}회). 재검토 결과는 새 리포트로 보관함에 남고, 실패하면 차감되지 않습니다.`
    : '재검토 결과는 새 리포트로 보관함에 남습니다.';

/** 학기 목록 표기 — '1-1·1-2'(원문 그대로, 용어집: 풀어쓰기는 학년 단위 요약에만). */
const semesterList = (semesters: string[]): string => semesters.join('·');

/** 누락 학기 요약 — 한 학년이면 "1학년 성적(1-1·1-2)", 여러 학년이면 학기 나열. */
function missingSummary(missing: string[]): string {
  const years = new Set(missing.map((s) => s.charAt(0)));
  return years.size === 1
    ? `${[...years][0]}학년 성적(${semesterList(missing)})`
    : `${semesterList(missing)} 성적`;
}

const gradeLabelOf = (w: CoverageWarning): string =>
  labelOf(GRADE_OPTIONS, w.profileGrade) || '미설정';

/**
 * 내신 학기 커버리지·완결성(074) — 입시카드 배너, 6장 카드·재검토·종합분석 확인 모달, 결과 화면 표식의 카피 SSoT.
 * 화면 용어는 "예비 판정·입력 범위·남은 학기"만 — 내부 용어(stage·coverage·outlook)는 노출하지 않는다.
 * 학년 라벨은 `GRADE_OPTIONS` 재사용(중복 정의 금지), 학기는 `1-1` 원문.
 */
export const NAESIN_COVERAGE_COPY = {
  bannerTitle: (w: CoverageWarning): string => {
    switch (w.kind) {
      case 'GRADE_AHEAD': {
        return `학년이 ${gradeLabelOf(w)}로 설정돼 있는데 ${w.aheadYear}학년 성적이 있습니다`;
      }
      case 'PARTIAL': {
        return `${missingSummary(w.missing)}이 없습니다`;
      }
      default: {
        return '학년을 설정하면 빠진 학기를 확인해 드립니다';
      }
    }
  },
  bannerBody: (w: CoverageWarning): string => {
    switch (w.kind) {
      case 'GRADE_AHEAD': {
        return '학년을 바꾸면 빠진 학기를 정확히 확인할 수 있습니다. 이대로 분석하면 입력된 학기만으로 판정됩니다.';
      }
      case 'PARTIAL': {
        return '이대로 분석하면 입력된 학기만으로 판정됩니다. 빠진 학기를 채우면 AI 분석이 더 정확해집니다.';
      }
      default: {
        return '기본 정보에서 학년을 저장하면 어떤 학기가 빠졌는지 알려 드립니다.';
      }
    }
  },
  confirmTitle: '입력되지 않은 학기가 있어요',
  /** 확인 모달 본문 — GRADE_AHEAD 는 학년 갱신 권고를 앞에 둔다. GRADE_UNKNOWN 은 모달 대상이 아니다(빈 문자열). */
  confirmBody: (w: CoverageWarning): string => {
    if (w.kind === 'GRADE_UNKNOWN') return '';
    const gap = w.missing.length > 0 ? ` ${missingSummary(w.missing)}이 없습니다.` : '';
    const head =
      w.kind === 'GRADE_AHEAD'
        ? `학년이 ${gradeLabelOf(w)}로 설정돼 있는데 ${w.aheadYear}학년 성적이 있습니다. 학년을 갱신하면 빠진 학기를 정확히 확인할 수 있습니다.${gap}`
        : `${missingSummary(w.missing)}이 없습니다.`;
    return `${head} 이대로 분석하면 입력된 학기만으로 판정됩니다.`;
  },
  /** 커버리지 조회 실패(pending 아님, 응답 오류) — 판정을 못 했음을 알리고 진행 여부를 묻는다. 경고 생략으로 이용권을 쓰지 않게. */
  checkFailedTitle: '내신 입력 현황을 확인하지 못했어요',
  checkFailedBody:
    '연결 문제로 빠진 학기가 있는지 확인하지 못했습니다. 이대로 분석하면 입력된 학기만으로 판정됩니다.',
  proceed: '그대로 진행',
  goNaesin: '성적 입력하러 가기',
  goGrade: '학년 바꾸기',
  setGrade: '학년 설정',
  goNaesinShort: '성적 입력',
  /** 결과 화면(수시 6장) — 3-1 미완 리포트 헤더 배지. */
  preliminaryBadge: '예비 판정',
  /** 헤더 요약 항목 [라벨, 값] — `ReportHeader.items` 형태 그대로. */
  rangeItem: (first: string, last: string): [string, string] => [
    '내신 입력 범위',
    `${first}~${last}`,
  ],
  /** 카드 한 줄(data-model §5.2 4문구) — 수치는 서버 역산값 그대로. */
  outlook: (o: CardOutlook): string => {
    const range = semesterList(o.remainingSemesters);
    if (o.reach === 'UNREACHABLE') {
      return '현재 입력 범위로는 남은 학기만으로 합격선 도달이 어렵습니다';
    }
    if (o.reach === 'ANY') {
      return '현재 평균으로 합격선을 충분히 유지합니다';
    }
    const target = o.targetAvg == null ? '-' : o.targetAvg.toFixed(2);
    return o.relation === 'KEEP'
      ? `남은 학기(${range}) 평균 ${target}등급 이내면 합격선 유지`
      : `남은 학기(${range}) 평균 ${target}등급이면 합격선 도달`;
  },
} as const;

/**
 * 비로그인 샘플 뷰(077) — 배너·잠금 안내·모달 대상명·검사 프레임 카피 SSoT.
 * 실제 총건수·정책값은 적지 않는다(FR-012 — "전체" 로만 말한다). 용어는 용어집 대표어만.
 * `targetName` 은 `LoginGateModal` 의 `<b>` 바인딩 — GNB 라벨(`workspaceNav.ts`)과 같은 표기.
 */
export const SAMPLE_PREVIEW_COPY = {
  banner: {
    title: '샘플 성적으로 보는 예시 화면입니다',
    body: '지금 보이는 수치는 예시입니다. 로그인하면 저장한 내 성적으로 같은 화면이 채워집니다.',
    cta: '로그인하고 내 성적으로 분석하기',
  },
  targetName: { jeongsi: '정시 분석', susi: '수시 분석', tests: '진로심리검사' },
  listLocked: {
    title: '로그인하면 조건에 맞는 모집단위 전체를 볼 수 있습니다',
    body: '내 성적 기준으로 안정·적정·소신·위험이 나뉘고, 지역·대학·검색으로 좁혀 볼 수 있습니다.',
  },
  testsFrame: '검사 4종이 각각 무엇을 알려주는지와 결과가 어떤 모양인지 미리 볼 수 있습니다.',
  /** 검사 샘플 결과 발췌 카드 제목·설명. */
  testsResult: {
    title: '결과는 이런 모양입니다',
    body: '검사를 마치면 영역별 프로필과 강점 요약이 함께 정리됩니다.',
  },
} as const;

/**
 * 통합 검색(078) — 히어로 검색창·GNB 런처·결과 패널·빈 상태·로그인 유도 카피 SSoT.
 * 용어는 대학·학과·정시·수시·보유만 쓴다(FR-021 — 유사도·행 식별자 같은 내부어 금지).
 */
export const SEARCH_COPY = {
  placeholderHero: '대학·학과·기능 검색 (예: 서울대 컴퓨터)',
  placeholderPanel: '대학·학과·기능 검색',
  launcherAria: '대학·학과·기능 검색',
  heroSubmit: '검색',
  suggestionLabel: '추천',
  panelAria: '통합 검색',
  close: '검색 닫기',
  group: { jeongsi: '정시', susi: '수시', function: '기능' },
  yearHeader: (year: number): string => `${year}학년도`,
  subtitle: { jeongsi: '정시 분석 › 학과 상세', susi: '수시 분석 › 전형별 등급' },
  access: { owned: '보유', locked: '미보유' },
  idle: { recent: '최근 검색', shortcuts: '바로가기', applyCards: '내 지원 카드' },
  applyCardChip: (university: string, department: string | null, group: '정시' | '수시'): string =>
    `${university}${department ? ` ${department}` : ''} · ${group}`,
  loading: '대학·학과 목록을 불러오는 중이에요',
  none: (q: string): string => `'${q}' 에 맞는 대학·학과가 없어요`,
  noneSub: '현재 수록된 대학 기준이에요',
  universityOnly: (u: string): string => `'${u}' 학과 전체 보기`,
  goJeongsiList: '정시 목록 보기',
  goSusiList: '수시 목록 보기',
  truncated: '더 입력해 좁혀 보세요',
  error: '검색을 잠시 쓸 수 없어요',
  retry: '다시 시도',
  footerKeys: '↑↓ 이동 · ↵ 열기 · ESC 닫기',
  footerJeongsi: '찾는 학과가 없나요? 정시 목록에서 직접 찾기',
  susiDeepLinkMissing: '찾던 학과를 이번 학년도 전형에서 찾지 못해 첫 전형을 보여드려요.',
  /** 비로그인 대학·학과 결과 클릭 → 로그인 화면 맥락 한 줄. 이름이 없으면(새로고침) 일반 문구. */
  loginContext: (target: string | null): string =>
    target
      ? `로그인하면 ${target} 분석으로 바로 이동해요.`
      : '로그인하면 검색한 학과 분석으로 바로 이동해요.',
} as const;
